import { ACCEPT_DECIMAL } from '../constants/decimals';

export const CommaFormatted = (
  amount: any,
  name?: string,
  setFieldValue?: any,
): string => {
  const numberValue = Number(String(amount)?.replaceAll(',', '') ?? '');
  const hasDecimal = numberValue % 1 !== 0;
  const value = hasDecimal ? numberValue.toFixed(ACCEPT_DECIMAL) : numberValue.toLocaleString();
  if (name != null && Boolean(amount)) setFieldValue(name, !isNaN(numberValue) ? value : '');
  return value;
};

export const convertNumberWithCommas = (
  value?: number | string,
  showDecimal?: boolean,
  fixValue?: number,
): any => {
  const NumberValue = Number(value)?.toFixed(fixValue ?? ACCEPT_DECIMAL);
  value = `${String(value) ?? ''}`;
  const x: any = NumberValue?.toString().split('.');
  let x1 = x[0];
  const x2 = '.' + String(x[1] ?? '');
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1' + ',' + '$2');
  }
  const finalValue = showDecimal ?? false ? String(x1 ?? '') + String(x2 ?? '') : x1;
  return finalValue;
};

/**
 * Thousand-separators only — does not pad/truncate decimals (no ACCEPT_DECIMAL).
 * Use for table cells when schema does not pin a fixed decimal count.
 */
export const convertNumberWithCommasPreserve = (value?: number | string): string => {
  if (value == null || value === '') return '';

  const formatNumericString = (raw: string): string => {
    const [intPart, decPart] = raw.split('.');
    let x1 = intPart.replace(/^-/, '');
    const negative = intPart.startsWith('-');
    const rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1,$2');
    }
    const withSign = `${negative ? '-' : ''}${x1}`;
    return decPart != null ? `${withSign}.${decPart}` : withSign;
  };

  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').trim();
    if (/^-?\d+(\.\d+)?$/.test(cleaned)) {
      return formatNumericString(cleaned);
    }
  }

  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);

  const raw =
    /e/i.test(String(num))
      ? num.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
      : String(num);

  return formatNumericString(raw);
};

export const setDashAsEmptyValue = (
  values: string | number | null | undefined,
): string | number => {
  return values === null || values === '' || values === undefined ? '-' : values;
};

export const toCamelCase = (str: string): string => {
  return str
    ?.replace(/[^a-zA-Z0-9\s]/g, '')
    ?.split(/\s+/)
    ?.map((word, index) => {
      if (index === 0) {
        return word?.toLowerCase();
      }
      return word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase();
    })
    ?.join('');
};

const SUPPORTED_IMAGE_FORMATS = ['jpg', 'jpeg', 'gif', 'png'];

function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

export const handleOpenFile = (fileName: string, fileContent: string, preOpenedWindow?: Window | null): any => {
  const fileExtension = getFileExtension(fileName);

  if (SUPPORTED_IMAGE_FORMATS.includes(fileExtension)) {
    const image = new Image();
    image.src = `data:image/${fileExtension};base64,` + fileContent;
    const w: any = preOpenedWindow ?? window.open('');
    if (w == null) return;
    w.document.write(image.outerHTML);
    w.document.close();
  } else if (fileExtension === 'pdf') {
    const pdfWindow: any = preOpenedWindow ?? window.open('');
    if (pdfWindow == null) return;
    pdfWindow.document.write(
      `<iframe width='100%' height='100%' src='data:application/pdf;base64, ` +
        encodeURI(fileContent) +
        "'></iframe>",
    );
  } else {
    if (preOpenedWindow) preOpenedWindow.close();
    const aElement = document.createElement('a');
    const downloadLink = `data:application/${fileExtension};base64,` + fileContent;
    aElement.setAttribute('download', fileName);
    aElement.setAttribute('href', downloadLink);
    aElement.click();
  }
};

export async function openFileAsync(
  fetchFn: () => Promise<{ name?: string; content?: string } | null | undefined>,
): Promise<void> {
  const result = await fetchFn();

  if (!result?.content || !result?.name) return;

  const fileExtension = getFileExtension(result.name);
  const byteCharacters = atob(result.content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  if (SUPPORTED_IMAGE_FORMATS.includes(fileExtension)) {
    const blob = new Blob([byteArray], { type: `image/${fileExtension}` });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } else if (fileExtension === 'pdf') {
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } else {
    const blob = new Blob([byteArray], { type: `application/${fileExtension}` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.name;
    a.click();
    URL.revokeObjectURL(url);
  }
}
